import React, { FC } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import BodyCompositionChart from '../BodyCompositionChart/BodyCompositionChart';
import { BodyComposition } from '../../../services/DataFormatService';
import boddVariables from '../../../variables.module.scss';
import DataList, { DataListItem } from '../../DataList/DataList';
import PopoverComponent from '../../PopoverComponent/PopoverComponent';

const BasicInfo = () => (
    <div>
        <div>
            <Typography variant="h3">
                Fat Mass
            </Typography>
            <Typography>
                Fat Mass represents the total weight of all the fat in the body, both
                essential
                and storage fat.
            </Typography>
        </div>
        <div>
            <Typography variant="h3" sx={{ pt: 4 }}>
                Total Body Water
            </Typography>
            <Typography>
                Total water content in the body. Vital for cellular function, temperature
                regulation, and metabolism.
            </Typography>
        </div>
        <div>
            <Typography variant="h3" sx={{ pt: 4 }}>
                Total Body Protein
            </Typography>
            <Typography>
                The total amount of protein in the body, primarily found in:
            </Typography>
            <ul>
                <li>
                    Muscles: Structural and contractile proteins.
                </li>
                <li>
                    Organs and Tissues: Functional proteins, including enzymes and
                    hormones.
                </li>
            </ul>
        </div>
        <div>
            <Typography variant="h3" sx={{ pt: 4 }}>
                Total Body Bone Material (TBBM)
            </Typography>
            <Typography>
                The total mineral content of bones, primarily composed of: Calcium and
                Phosphorus, which provide bone density and strength.
            </Typography>
        </div>
    </div>
);

const AdvancedInfo = () => (
    <div>
        <div>
            <Typography>
                Based on the Cohn and Snyder Model: a cellular-level model of body composition that breaks down the body
                into specific compartments to provide a detailed understanding of its chemical and cellular makeup.
            </Typography>
        </div>
        <div>
            <Typography variant="h3" sx={{ pt: 4 }}>
                Body Cell Mass
            </Typography>
            <Typography>
                The total mass of all metabolically active, living cells in the body. This includes muscle cells, organ
                cells, and other tissues that actively contribute to energy metabolism.
            </Typography>
        </div>
        <div>
            <Typography variant="h3" sx={{ pt: 4 }}>
                Intracellular Water
            </Typography>
            <Typography>
                The portion of total body water contained within cells. It supports cell structure and is vital for
                metabolic processes inside cells.
            </Typography>
        </div>
        <div>
            <Typography variant="h3" sx={{ pt: 4 }}>
                Extracellular Water
            </Typography>
            <Typography>
                The portion of total body water outside cells, including fluid in blood plasma, lymph, and interstitial
                spaces.
            </Typography>
        </div>
        <div>
            <Typography variant="h3" sx={{ pt: 4 }}>
                Fat-Free Extracellular Solids (FFECS)
            </Typography>
            <Typography>
                Non-fat, non-water components in the extracellular space, such as connective tissues, minerals, and
                structural components of the body.
            </Typography>
        </div>
        <div>
            <Typography variant="h3" sx={{ pt: 4 }}>
                Intracellular Protein
            </Typography>
            <Typography>
                Protein content inside cells, primarily found in muscles, organs, and other tissues critical for
                metabolic and structural functions.
            </Typography>
        </div>
        <div>
            <Typography variant="h3" sx={{ pt: 4 }}>
                Extracellular Protein
            </Typography>
            <Typography>
                Protein found outside cells, such as in blood plasma and connective tissue (e.g., collagen and enzymes).
            </Typography>
        </div>
    </div>
);

const MetabolocIndicatorInfo = () => (
    <div>
        <Typography>
            Basal Metabolic Rate (BMR) is the amount of energy (calories) your body requires to maintain basic
            physiological functions while at rest. These functions include essential processes such as breathing,
            circulation, cell production, and maintaining body temperature.
        </Typography>
    </div>
);

interface OverviewComponentProps {
    bodyComposition: BodyComposition;
}

const Dot = (colour: string) => (
    <Box
        sx={{
            width: 20,
            height: 20,
            bgcolor: colour,
            borderRadius: '50%',
            mt: '2px',
        }}
    />
);

const OverviewComponent: FC<OverviewComponentProps> = (props) => {
    const { bodyComposition } = props;

    const canCalculateTotalMass = bodyComposition.bodyFatMass
        && bodyComposition.totalBodyWaterMass
        && bodyComposition.totalBodyProteinMass
        && bodyComposition.totalBodyBoneMineralMass;

    const totalMass = canCalculateTotalMass ? bodyComposition.bodyFatMass!
        + bodyComposition.totalBodyWaterMass!
        + bodyComposition.totalBodyProteinMass!
        + bodyComposition.totalBodyBoneMineralMass! : 0;

    const bodyFatPercentage = canCalculateTotalMass ? (bodyComposition.bodyFatMass! / totalMass) * 100 : 0;
    const bodyWaterPercentage = canCalculateTotalMass ? (bodyComposition.totalBodyWaterMass! / totalMass) * 100 : 0;
    const bodyProteinPercentage = canCalculateTotalMass ? (bodyComposition.totalBodyProteinMass! / totalMass) * 100 : 0;
    const bodyBoneMaterialPercentage = canCalculateTotalMass ? (bodyComposition.totalBodyBoneMineralMass! / totalMass) * 100 : 0;

    const basicList: DataListItem[] = [
        ...(bodyComposition.bodyFatMass ? [{
            name: 'Fat Mass',
            value: `${(bodyComposition.bodyFatMass / 1000).toFixed(1)} kg • ${bodyFatPercentage.toFixed(1)}%`,
            prefix: (Dot(boddVariables.brandPink)),
        }] : []),
        ...(bodyComposition.totalBodyWaterMass ? [{
            name: 'Total Body Water',
            value: `${(bodyComposition.totalBodyWaterMass / 1000).toFixed(1)} kg • ${bodyWaterPercentage.toFixed(1)}%`,
            prefix: (Dot(boddVariables.brandBlue)),
        }] : []),
        ...(bodyComposition.totalBodyProteinMass ? [{
            name: 'Total Body Protein',
            value: `${(bodyComposition.totalBodyProteinMass / 1000).toFixed(1)} kg • ${bodyProteinPercentage.toFixed(1)}%`,
            prefix: (Dot(boddVariables.brandRed)),
        }] : []),
        ...(bodyComposition.totalBodyBoneMineralMass ? [{
            name: 'Total Body Bone Material',
            value: `${(bodyComposition.totalBodyBoneMineralMass / 1000).toFixed(1)} kg • ${bodyBoneMaterialPercentage.toFixed(1)}%`,
            prefix: (Dot(boddVariables.brandYellow)),
        }] : []),
    ];

    const advancedList: DataListItem[] = [
        ...(bodyComposition.bodyCellMass ? [{
            name: 'Body Cell Mass',
            value: `${(bodyComposition.bodyCellMass / 1000).toFixed(1)} kg`,
        }] : []),
        ...(bodyComposition.intraCellularWaterMass ? [{
            name: 'Intra Cellular Water',
            value: `${(bodyComposition.intraCellularWaterMass / 1000).toFixed(1)} kg`,
        }] : []),
        ...(bodyComposition.extraCellularWaterMass ? [{
            name: 'Cellular Water',
            value: `${(bodyComposition.extraCellularWaterMass / 1000).toFixed(1)} kg`,
        }] : []),
        ...(bodyComposition.fatFreeExtraCellularSolidsMass ? [{
            name: 'Fat-Free Extracellular Solids',
            value: `${(bodyComposition.fatFreeExtraCellularSolidsMass / 1000).toFixed(1)} kg`,
        }] : []),
        ...(bodyComposition.intraCellularProteinMass ? [{
            name: 'Intra Cellular Protein',
            value: `${(bodyComposition.intraCellularProteinMass / 1000).toFixed(1)} kg`,
        }] : []),
        ...(bodyComposition.extraCellularProteinMass ? [{
            name: 'Extra Cellular Protein',
            value: `${(bodyComposition.extraCellularProteinMass / 1000).toFixed(1)} kg`,
        }] : []),
    ];

    const metabolicIndicatorList: DataListItem[] = [
        ...(bodyComposition.basalMetabolicRate ? [{
            name: 'Basal Metabolic Rate',
            value: `${bodyComposition.basalMetabolicRate} kCal / day`,
        }] : []),
    ];

    const indicesList: DataListItem[] = [
        ...(bodyComposition.bodyAdiposityIndex ? [{
            name: 'Body Adiposity Index',
            value: `${bodyComposition.bodyAdiposityIndex!.toFixed(2)}`,
        }] : []),
        ...(bodyComposition.abdominalVolumeIndex ? [{
            name: 'Abdominal Volume Index',
            value: `${bodyComposition.abdominalVolumeIndex!.toFixed(2)}`,
        }] : []),
        ...(bodyComposition.conicityIndex ? [{
            name: 'Conicity Index',
            value: `${bodyComposition.conicityIndex!.toFixed(2)}`,
        }] : []),
    ];

    return (
        <div data-testid="OverviewComponent">
            <Typography
                variant="h2"
                sx={{ pt: 2 }}
            >
                Overview
            </Typography>
            <Divider sx={{ borderColor: boddVariables.black, mt: 2, mb: 4 }} />
            <BodyCompositionChart
                fatMass={bodyComposition.bodyFatMass!}
                totalBodyWater={bodyComposition.totalBodyWaterMass!}
                totalBodyProtein={bodyComposition.totalBodyProteinMass!}
                totalBodyBoneMaterial={bodyComposition.totalBodyBoneMineralMass!}
                totalMass={(bodyComposition.bodyMassGrams ?? 0) / 1000}
            />
            <Typography
                variant="h3"
                component="h3"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    pt: 4,
                }}
            >
                <PopoverComponent>
                    <BasicInfo />
                </PopoverComponent>
                Basic
            </Typography>
            <Divider />
            <DataList dataListItems={basicList} />
            <Typography
                variant="h3"
                component="h3"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    pt: 4,
                }}
            >
                <PopoverComponent>
                    <AdvancedInfo />
                </PopoverComponent>
                Advanced
            </Typography>
            <Divider />
            <DataList dataListItems={advancedList} />
            <Typography
                variant="h3"
                component="h3"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    pt: 4,
                }}
            >
                <PopoverComponent>
                    <MetabolocIndicatorInfo />
                </PopoverComponent>
                Metabolic Indicator
            </Typography>
            <Divider />
            <DataList dataListItems={metabolicIndicatorList} />
            <Typography
                variant="h3"
                component="h3"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    pb: 0.5,
                    pt: 4,
                }}
            >
                Indices
            </Typography>
            <Divider />
            <DataList dataListItems={indicesList} />
            <br />
        </div>
    );
};

export default OverviewComponent;
